import {useMutation, useQuery} from "@apollo/client";
import {yupResolver} from "@hookform/resolvers/yup";
import {Suspense} from "react";
import {FormProvider, useForm} from "react-hook-form";
import * as Yup from "yup";

import {ImageInputField} from "@/components/form/fields/ImageInputField/ImageInputField.tsx";
import {FormModal} from "@/components/form/overlay/FormModal.tsx";
import {significantDecimalFormatLabeled} from "@/lib/format.tsx";
import {UpErrorBoundary} from "@/lib/handlers/UpErrorBoundary.tsx";
import {URL_ORIGINAL_WEBSITE} from "@/lib/links.ts";
import {
  GQL_SAVE_SLA_REPORT_GLOBAL_LOGO,
  GQL_SLA_REPORT_GLOBAL_LOGO_QUERY,
  SLAReportGlobalLogo,
} from "@/lib/queries/reports.ts";
import {ResponseStatus} from "@/lib/types/common.ts";

const MAX_LOGO_FILE_SIZE = 1024 * 1024;
const FORM_ID = "sla-report-logo-form";

interface SLAReportLogoFields {
  logo?: string | FileList | null;
}

type SLAReportLogoKeys = keyof SLAReportLogoFields;

export const logoValidator = Yup.mixed<FileList>()
  .nullable()
  .test(
    "is-valid-size",
    `Maximum logo file size is ${significantDecimalFormatLabeled(MAX_LOGO_FILE_SIZE / (1024 * 1024), "megabyte")}`,
    (value) => (value instanceof FileList && value.length > 0 ? value[0].size <= MAX_LOGO_FILE_SIZE : true)
  );

interface SLAReportLogoDrawerProps {
  show: boolean;
  hide: () => void;
}

export const SLAReportLogoDrawer = ({show, hide}: SLAReportLogoDrawerProps) => {
  const {data} = useQuery<SLAReportGlobalLogo>(GQL_SLA_REPORT_GLOBAL_LOGO_QUERY);
  const [saveLogo] = useMutation(GQL_SAVE_SLA_REPORT_GLOBAL_LOGO, {refetchQueries: [GQL_SLA_REPORT_GLOBAL_LOGO_QUERY]});

  const onSubmit = async (data: SLAReportLogoFields) => {
    const resp = await saveLogo({
      variables: {
        logo:
          data.logo instanceof FileList && data.logo.length > 0 ? data.logo[0] : data.logo === null ? null : undefined,
      },
    });

    if (resp.data?.response?.status === ResponseStatus.SUCCESS) {
      hide();
    } else {
      if (resp.data?.response?.message) {
        Object.entries(resp.data.response.message).forEach(([fieldName, messageList]) => {
          form.setError(fieldName as SLAReportLogoKeys, {message: (messageList as string[])[0]});
        });
      }
    }
  };

  const form = useForm<SLAReportLogoFields>({
    resolver: yupResolver<SLAReportLogoFields>(
      Yup.object({
        logo: logoValidator,
      })
    ),
    defaultValues: {
      logo: data?.slaReportGlobalLogo.logo_url,
    },
  });

  if (!data) return null;

  return (
    <Suspense>
      <UpErrorBoundary>
        <FormModal size="lg" title="Global Report Settings" onCancel={hide} show={show} formId={FORM_ID} centered>
          <FormProvider {...form}>
            <form id={FORM_ID} onSubmit={form.handleSubmit(onSubmit)} noValidate>
              <ImageInputField
                name="logo"
                label="Default Report Logo"
                tooltip="This logo will be applied to all SLA reports unless a report has its own logo specified."
                helpText={!form.watch("logo") ? "Currently using the Uptime.com default logo." : ""}
                variant="report"
                fallbackPreviewUrl={`${URL_ORIGINAL_WEBSITE}/static/img/global/logo-uptime-pdf.jpg`}
              />
            </form>
          </FormProvider>
        </FormModal>
      </UpErrorBoundary>
    </Suspense>
  );
};
